import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2"; // Grid version 2
import CircleIcon from '@mui/icons-material/Circle';
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

import ActionButton from "../../account/ActionButton";

import { useClient } from "../../../context/ClientProvider";
import { useUtils } from "../../../context/UtilsProvider";
import { useArrangement } from "../../../context/ArrangementProvider";
import { useLiveLoans } from "../../../context/LiveLoansProvider";
import { formatCurrency } from '../../../utils/formatCurrency';

// Methods and properties for pie chart calculation
const pieChartSize = 1800;

/**
 * Get paid pie value
 * @param {*} totalPaid
 * @param {*} balance
 * @returns
 */
const getPaidPieValue = (totalPaid, balance) => {
  if (!totalPaid || !balance) {
    return 0;
  }

  // Prepare values
  const totalCredit = totalPaid + balance;

  // Calculate paid section of pie chart
  const percentagePaid = Math.round((totalPaid / totalCredit) * 100);
  const paidPieValue = pieChartSize * (percentagePaid / 100);

  return paidPieValue;
};

/**
 * Create data
 * @param {*} id
 * @param {*} title
 * @returns
 */
function createData(id, title, value) {
  return { id, title, value };
}

/**
 * Arrangement widget component
 * @param {*} param0
 * @returns
 */
function LiveLoansArrangementWidget({ paymentDateChanges, defaultStatus }) {
  const theme = useTheme();
  const { capitaliseWords } = useUtils();
  const { lastSavedPlan, hasArrangement, paymentMethods } = useArrangement();

  const { useGetConfig } = useClient();
  const { clientConfig } = useGetConfig();

  const { allowPaymentDateChanges } = useLiveLoans();

  // Local palette for gradient shading
  const [localPalette, setLocalPalette] = useState(null); // Used for applying gradient

  useEffect(() => {
    if (clientConfig && clientConfig?.theme?.palette) {
      setLocalPalette(clientConfig.theme.palette);
    }
  }, [clientConfig]);

  let repaymentData = [
    createData(
      "next-repay-amount",
      "Next repayment amount: ",
      `£${lastSavedPlan?.nextPaymentAmount?.toFixed(2)}`
    ),
    createData(
      "regular-repay-amount",
      "Regular repayment amount: ",
      `£${lastSavedPlan?.instalmentAmount?.toFixed(2)}`
    ),
    createData(
      "repay-due-date",
      "Repayment due date:",
      dayjs(lastSavedPlan?.nextPaymentDate)?.format("DD/MM/YYYY")
    ),
    createData(
      "pay-method",
      "Payment method:",
      capitaliseWords(
        lastSavedPlan?.paymentMethod
          ? Object.keys(paymentMethods).find(
            (key) => paymentMethods[key] === lastSavedPlan?.paymentMethod
          )
          : ""
      )
    )
  ];

  // Calculate pie chart values
  const paidPieValue = getPaidPieValue(lastSavedPlan?.paidToDate, lastSavedPlan?.totalRemaining);
  const outstandingPieValue = pieChartSize - paidPieValue;

  let repaymentChartData = [
    {
      id: 0,
      label: `Paid amount`,
      value: lastSavedPlan?.paidToDate || 0,
      pieValue: paidPieValue,
      color: theme.palette.secondary.dark,
    },
    {
      id: 1,
      label: `Outstanding`,
      value: lastSavedPlan?.totalRemaining || 0,
      pieValue: outstandingPieValue,
      color: "#f5f5f5",
    },
  ];

  // Responsive margin logic
  const getMargin = (width) => {
    if (width < 600) {
      return { top: 120, right: 90, left: 90, bottom: 80 };
    } else {
      return { top: 60, right: 50, left: 50, bottom: 50 };
    }
  };

  const [margin, setMargin] = useState(getMargin(window.innerWidth));
  

  useEffect(() => {
    const handleResize = () => {
      const newMargin = getMargin(window.innerWidth);
      setMargin(newMargin);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);


  // Custom legend item
  const CustomLegend = () => {
    return (
      <Box
        sx={{
          textAlign: "center",
          position: "absolute",
          transform: "translateY(-25%)",
          margin: 1,
          height: 75,
        }}
      >
        {repaymentChartData.map((entry, index) => (
          <Box
            key={`item-${index}`}
            sx={{
              display: "flex",
              alignItems: "center",
              margin: 1,
            }}
          >
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: entry.color,
                marginRight: 1,
                borderRadius: "50%",
              }}
            />
            <Typography sx={{ color: "#f5f5f5", fontWeight: "bold" }}>
              {entry.label}: {formatCurrency(entry.value)}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return localPalette && hasArrangement && lastSavedPlan ? (
    <Grid>
      <Box
        id='repayment-plan-container'
        sx={{
          backgroundColor: "primary.dark",
          color: "primary.contrastText",
          borderLeft: `10px solid ${localPalette.secondary.dark}`
        }}
      >
        <Typography
          fontWeight={"bold"}
          fontSize={24}
          paddingTop={1}
          marginLeft={2}
        >
          My repayment plan
        </Typography>

        {/* About Repayment Section */}
        <TableContainer sx={{ px: 1, py: 1 }}>
          <Table
            sx={{ minWidth: "100%" }}
            size='small'
            aria-label='repayment plan table'
          >
            <TableBody>
              {repaymentData.map((row) => (
                <TableRow
                  key={row.title}
                  sx={{
                    "td, th": { border: 0 },
                    th: { color: "#FFF" },
                    td: { color: "#FFF" },
                  }}
                >
                  <TableCell
                    sx={{ fontWeight: "bold" }}
                    component='th'
                    scope='row'
                  >
                    {row.title}
                  </TableCell>
                  <TableCell id={row.id} align='right'>
                    {row.value}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pie Chart Section */}
        <Box
          sx={{
            position: "relative",
            top: "55px",
            width: "100%",
            height: "197px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <ResponsiveContainer width='100%' height={300}>
            <PieChart width={300} height={300} margin={margin}>
              <Pie
                data={repaymentChartData}
                dataKey='pieValue'
                startAngle={180}
                endAngle={0}
                innerRadius={120}
                outerRadius={150}
                nameKey='label'
              >
                {repaymentChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <CustomLegend />
        </Box>

        {/* Call To Action */}
        <Box sx={{ textAlign: "center", pt: 2, pb: 4 }}>
          {defaultStatus ? <Box>
            <Typography sx={{ mx: 1.5, fontSize: 14 }}>
              * Your account has defaulted, meaning you are unable to change your current arrangement online. To discuss please contact us on <b>0333 003 0260</b>.
            </Typography>
          </Box> :
            !lastSavedPlan?.inPaymentWindow ?
              <>
                {
                  allowPaymentDateChanges(paymentDateChanges) ?
                    <Box>
                      <ActionButton
                        id='change-payment-date-btn'
                        buttonColor='secondary.dark'
                        buttonTextColor='primary.contrastText'
                        buttonTitle='Change payment date'
                        hoverColor='primary.contrastText'
                        hoverTextColor='secondary.dark'
                        target='/repayment/change-payment-date'
                      />
                    </Box> :
                    <Box>
                      <Typography sx={{ mx: 1.5, fontSize: 14 }}>
                        * You are unable to change your repayment date as you have made too many recent changes. To discuss please contact us on <b>0333 003 0260</b>.
                      </Typography>
                    </Box>
                }
              </> :
              <Box>
                <Typography sx={{ mx: 1.5, fontSize: 14 }}>
                  * You are unable to change your repayment date as you have a payment due soon. To discuss please contact us on <b>0333 003 0260</b>.
                </Typography>
              </Box>
          }
        </Box>

      </Box>
    </Grid>
  ) : localPalette && (
    <Grid>
      <Box
        id='repayment-plan-container'
        sx={{
          backgroundColor: "primary.dark",
          color: "primary.contrastText",
          borderRadius: 0,
          borderLeft: `10px solid ${localPalette.secondary.dark}`
        }}
      >
        <Typography
          fontWeight={"bold"}
          fontSize={24}
          paddingTop={1}
          marginLeft={2}
        >
          My repayment plan <CircleIcon sx={{ color: "#EA4278", fontSize: "18px", verticalAlign: "top" }} />
        </Typography>

        <Typography
          fontSize={14}
          paddingTop={1}
          paddingBottom={1}
          marginX={2}
          color='#FFF'
        >
          There's no active repayment plan set up on your account.{" "}
          Want to steadily reduce your balance with an affordable plan?{" "}
          Let's take a look at your options...
        </Typography>

        {/* Call To Action */}
        <Box sx={{ textAlign: "center", pt: 2, pb: 4 }}>
          <ActionButton
            id='contact-us-btn'
            buttonColor='secondary.dark'
            buttonTextColor='primary.contrastText'
            buttonTitle='Contact us'
            hoverColor='primary.contrastText'
            hoverTextColor='secondary.dark'
            target='/contact-us'
          />
        </Box>
      </Box>
    </Grid>
  );
};

LiveLoansArrangementWidget.propTypes = {
  paymentDateChanges: PropTypes.array.isRequired,
};

export default LiveLoansArrangementWidget;
